/**
 * Aspect — Overlay (Tailwind/HTML UI layer)
 * --------------------------------------------------------------
 * The ONLY module allowed to touch the DOM. It renders the catalog + budget +
 * selection info and translates user intent into app events (e.g.
 * `ui:swap-asset`). It never references engine entities or the socket directly;
 * it listens to engine app events and emits UI app events. This preserves the
 * DOM-decoupling rule for all PlayCanvas scripts.
 */

import overlayHtml from "./overlay.html?raw";
import { getAiProvider, setAiProvider, PROVIDER_OPTIONS } from "../ai_provider.js";
import { loadSavedPlan, saveSavedPlan } from "../plan_store.js";
import { hasSessionTour } from "../session_tour_store.js";
import { fetchActivePlanImage } from "../plan_analyzer.js";
import { orchUrl } from "../endpoints.js";
import { compressImageDataUrl } from "../image_compress.js";
import { DEFAULT_FLOOR_COUNT, DEFAULT_ACTIVE_FLOOR } from "../scene/floor_defaults.js";
import { DEFAULT_INITIAL_HOUR } from "../scene/day_night_cycle.js";
import { itemThumb, renderedThumb, categoryIcon, CATEGORY_COLORS, CATEGORY_LABELS } from "./catalog_thumbs.js";
import { formatGelPrice, partnerBrandForId, aspectSalePrice, getCurrency, setCurrency } from "../currency.js";
import { parseRestyleIntent } from "../restyle_intent.js";

/** localStorage key for the last uploaded 2D render (shown next to the minimap). */
const PLAN_IMAGE_KEY = "aspect:plan-image";

/** localStorage key for the object hover-info cards toggle (default ON). */
const HOVER_UI_KEY = "aspect:hover-ui";

/** Apple-style ease-out (approx. cubic-bezier(0.22, 1, 0.36, 1)). */
function hoverEaseOut(t) {
  const p = 1 - Math.min(1, Math.max(0, t));
  return 1 - p * p * p * p;
}

export class Overlay {
  /**
   * @param {import('playcanvas').AppBase} app
   * @param {HTMLElement} mount
   * @param {{ demo?: boolean }} [opts]
   */
  constructor(app, mount, opts = {}) {
    this.app = app;
    this.demo = Boolean(opts.demo);
    this.readOnly = Boolean(opts.readOnly);
    this.sandbox = Boolean(opts.sandbox);
    mount.innerHTML = overlayHtml;
    this.root = mount;
    if (this.readOnly) {
      mount.querySelector('[data-overlay="touch-plan-bar"]')?.remove();
    }

    this.budgetEl = mount.querySelector('[data-overlay="budget"]');
    this.roomNameEl = mount.querySelector('[data-overlay="room-name"]');
    this.roomSepEl = mount.querySelector('[data-overlay="room-sep"]');
    this.sitHintEl = mount.querySelector('[data-overlay="sit-hint"]');
    this.interactBtn = mount.querySelector('[data-overlay="interact-btn"]');
    this.countEl = mount.querySelector('[data-overlay="count"]');
    this.savingsEl = mount.querySelector('[data-overlay="savings"]');
    this.savingsOldEl = mount.querySelector('[data-overlay="savings-old"]');
    this.savingsNewEl = mount.querySelector('[data-overlay="savings-new"]');
    this.savingsBadgeEl = mount.querySelector('[data-overlay="savings-badge"]');
    this.catalogPanel = mount.querySelector('[data-overlay="catalog-panel"]');
    this.catalogTitle = mount.querySelector('[data-overlay="catalog-title"]');
    this.catalogCategoriesEl = mount.querySelector('[data-overlay="catalog-categories"]');
    this.catalogItemsWrap = mount.querySelector('[data-overlay="catalog-items-wrap"]');
    this.catalogItemsEl = mount.querySelector('[data-overlay="catalog-items"]');
    this.catalogCountEl = mount.querySelector('[data-overlay="catalog-count"]');
    this.catalogEmptyEl = mount.querySelector('[data-overlay="catalog-empty"]');
    this.catalogSearch = mount.querySelector('[data-overlay="catalog-search"]');
    this.hintEl = mount.querySelector('[data-overlay="hint"]');
    this.selectionEl = mount.querySelector('[data-overlay="selection"]');
    this.selectionTextEl = mount.querySelector('[data-overlay="selection-text"]');
    this.selectionHintEl = mount.querySelector('[data-overlay="selection-hint"]');

    this.promptForm = mount.querySelector('[data-overlay="prompt-form"]');
    this.promptInput = mount.querySelector('[data-overlay="prompt-input"]');
    this.promptSend = mount.querySelector('[data-overlay="prompt-send"]');
    this.providerPicker = mount.querySelector('[data-overlay="ai-provider"]');
    this.providerBtn = mount.querySelector('[data-overlay="ai-provider-btn"]');
    this.providerLabel = mount.querySelector('[data-overlay="ai-provider-label"]');
    this.providerMenu = mount.querySelector('[data-overlay="ai-provider-menu"]');
    this.statusEl = mount.querySelector('[data-overlay="ai-status"]');
    this.statusTextEl = mount.querySelector('[data-overlay="ai-status-text"]');
    this.spinnerEl = mount.querySelector('[data-overlay="ai-spinner"]');

    this.attachBtn = mount.querySelector('[data-overlay="attach"]');
    this.touchUploadBtn = mount.querySelector('[data-overlay="touch-upload"]');
    this.touchResetBtn = mount.querySelector('[data-overlay="touch-reset"]');
    this.touchPlanStatusEl = mount.querySelector('[data-overlay="touch-plan-status"]');
    this.imageInput = mount.querySelector('[data-overlay="image-input"]');
    this.camToggleBtn = mount.querySelector('[data-overlay="cam-toggle"]');
    this.camToggleLabel = mount.querySelector('[data-overlay="cam-toggle-label"]');
    this.modeWalkBtn = mount.querySelector('[data-overlay="mode-walk"]');
    this.modePlanBtn = mount.querySelector('[data-overlay="mode-plan"]');
    this.modeOverheadBtn = mount.querySelector('[data-overlay="mode-overhead"]');
    this.currencyGelBtn = mount.querySelector('[data-overlay="currency-gel"]');
    this.currencyUsdBtn = mount.querySelector('[data-overlay="currency-usd"]');
    this.hoverInfoToggleBtn = mount.querySelector('[data-overlay="hover-info-toggle"]');
    // Revive Mode: freeze the view into an interactive 2D render (revive_mode.js).
    mount
      .querySelector('[data-overlay="revive-btn"]')
      ?.addEventListener("click", () => this.app.fire("ui:revive", {}));
    mount
      .querySelector('[data-overlay="revive-gallery-btn"]')
      ?.addEventListener("click", () => this.app.fire("ui:revive-gallery", {}));
    this.walkHintEl = mount.querySelector('[data-overlay="walk-hint"]');
    this.analysisToggleBtn = mount.querySelector('[data-overlay="analysis-toggle"]');
    this.analysisChevEl = mount.querySelector('[data-overlay="analysis-chev"]');
    this.marqueeEl = mount.querySelector('[data-overlay="marquee"]');
    this.selectionChipsEl = mount.querySelector('[data-overlay="selection-chips"]');
    this._structureSel = [];
    this.planPreview = mount.querySelector('[data-overlay="plan-preview"]');
    this.planPreviewBox = mount.querySelector('[data-overlay="plan-preview-box"]');
    this.planPreviewImg = mount.querySelector('[data-overlay="plan-preview-img"]');
    this.planPreviewPlaceholder = mount.querySelector('[data-overlay="plan-preview-upload"]');
    this.planPreviewLoading = mount.querySelector('[data-overlay="plan-preview-loading"]');
    this.minimapCanvas = mount.querySelector('[data-overlay="minimap-canvas"]');
    this.planOverlayToggle = mount.querySelector('[data-overlay="plan-overlay-toggle"]');
    this.planEditBtn = mount.querySelector('[data-overlay="plan-edit"]');
    this.planOverlayOpacity = mount.querySelector('[data-overlay="plan-overlay-opacity"]');
    this._planOverlayOn = false;
    this._planUploading = false;
    this.analysisEl = mount.querySelector('[data-overlay="analysis"]');
    this.analysisSummaryEl = mount.querySelector('[data-overlay="analysis-summary"]');
    this.analysisWarningEl = mount.querySelector('[data-overlay="analysis-warning"]');
    this.analysisSpinnerEl = mount.querySelector('[data-overlay="analysis-spinner"]');
    this.analysisConfidenceEl = mount.querySelector('[data-overlay="analysis-confidence"]');
    this.analysisBodyEl = mount.querySelector('[data-overlay="analysis-body"]');
    this.analysisDimsEl = mount.querySelector('[data-overlay="analysis-dims"]');
    this.analysisRoomsEl = mount.querySelector('[data-overlay="analysis-rooms"]');
    this.analysisScaleEl = mount.querySelector('[data-overlay="analysis-scale"]');
    this.analysisOpeningsEl = mount.querySelector('[data-overlay="analysis-openings"]');
    this.analysisFloorEl = mount.querySelector('[data-overlay="analysis-floor"]');
    this.analysisFloorsEl = mount.querySelector('[data-overlay="analysis-floors"]');
    this.analysisApplyBtn = mount.querySelector('[data-overlay="analysis-apply"]');
    this.floorPickerEl = mount.querySelector('[data-overlay="floor-picker"]');
    this.floorPickerRoot = mount.querySelector('[data-overlay="floor-picker-root"]');
    this.floorBtn = mount.querySelector('[data-overlay="floor-btn"]');
    this.floorMenu = mount.querySelector('[data-overlay="floor-menu"]');
    this.floorActiveLabel = mount.querySelector('[data-overlay="floor-active-label"]');
    this.floorCountLabel = mount.querySelector('[data-overlay="floor-count-label"]');
    const floorRoot = this.floorPickerRoot ?? mount;
    this.floorActiveRow = floorRoot.querySelector('[data-overlay="floor-active-row"]');
    this.floorCountRow = floorRoot.querySelector('[data-overlay="floor-count-row"]');
    this.analysisResetBtn = mount.querySelector('[data-overlay="analysis-reset"]');
    this.homeResetBtn = mount.querySelector('[data-overlay="home-reset"]');
    this.adminSaveBtn = mount.querySelector('[data-overlay="admin-save"]');
    this.adminSaveHint = mount.querySelector('[data-overlay="admin-save-hint"]');
    this.roomSavingsEl = mount.querySelector('[data-overlay="room-savings"]');
    this.roomSavingsListEl = mount.querySelector('[data-overlay="room-savings-list"]');
    this.roomSavingsTotalEl = mount.querySelector('[data-overlay="room-savings-total"]');
    this.addDoorBtn = mount.querySelector('[data-overlay="add-door"]');
    this.addWindowBtn = mount.querySelector('[data-overlay="add-window"]');
    this.timeSlider = mount.querySelector('[data-overlay="time-slider"]');
    this.timeLabel = mount.querySelector('[data-overlay="time-label"]');
    this.timeOfDayPanel = mount.querySelector('[data-overlay="time-of-day"]');

    this.hoverCard = mount.querySelector('[data-overlay="hover-card"]');
    this.hoverName = mount.querySelector('[data-overlay="hover-name"]');
    this.hoverPrice = mount.querySelector('[data-overlay="hover-price"]');
    this.hoverPriceOld = mount.querySelector('[data-overlay="hover-price-old"]');
    this.hoverSaleBadge = mount.querySelector('[data-overlay="hover-sale-badge"]');
    this.hoverSub = mount.querySelector('[data-overlay="hover-sub"]');
    this.hoverDims = mount.querySelector('[data-overlay="hover-dims"]');
    this.hoverBuy = mount.querySelector('[data-overlay="hover-buy"]');
    this.buySoon = mount.querySelector('[data-overlay="buy-soon"]');
    this.buySoonBack = mount.querySelector('[data-overlay="buy-soon-back"]');
    this._hoverPos = { x: 0, y: 0 };
    this._hoverTarget = null;
    this._hoverSlide = null;
    this._hoverShown = false;
    this._hoverHideTimer = null;
    this._hoverNullTimer = null;
    this._hoverLastId = null;
    this._hoverFlip = false;
    this._pendingHover = null;
    /** Object hover-info cards: ON unless the user switched them off before. */
    this._hoverUiOn = (() => {
      try {
        return localStorage.getItem(HOVER_UI_KEY) !== "off";
      } catch {
        return true;
      }
    })();
    this._fpLocked = false;
    this._camMode = "fp";
    if (this.hoverCard) {
      this.hoverCard.style.left = "0px";
      this.hoverCard.style.top = "0px";
    }
    this.app.on("prerender", () => this._tickHoverCard(this.app.dt ?? 0.016));

    this.selectedId = null;
    this.catalog = [];
    this._catalogQuery = "";
    this._catalogCategory = null;
    this._placingAsset = null;
    this._hudViewMode = "walk";
    this._analysisCollapsed = false;
    this.placementCount = 0;
    /** Last known storey counts from spec or live room (for the floor picker). */
    this._floorState = { activeFloor: DEFAULT_ACTIVE_FLOOR, floorCount: DEFAULT_FLOOR_COUNT };

    this._bindCatalogSearch();

    // Editable apartment spec state. `spec` is the working copy; `baselineSpec`
    // is the AI's original estimate that Reset restores.
    this.spec = null;
    this.baselineSpec = null;

    this._bind();
    if (!this.readOnly) this._bindPrompt();
    this._bindStructure();
    this._bindTimeOfDay();
    if (!this.readOnly) this._bindPlanOverlay();
    if (!this.readOnly) this._initProvider();
    this._initFloorPickers();
    this._userPlanImage = false;
    // Keep a custom 2D tour across refresh. Default SampleTour stays a blank
    // upload slot until the visitor uploads (or we restore their session).
    if (!this.readOnly && (!this.sandbox || hasSessionTour())) {
      this._restoreSavedPlan();
      this._restorePlanPreview();
    } else {
      this._showPlanState("empty");
    }

    // Keep the 2D-render frame the same size as the minimap.
    this.app.on("scene:room-loaded", () => {
      requestAnimationFrame(() => this._syncPlanPreviewSize());
    });
    window.addEventListener("resize", () => this._syncPlanPreviewSize());
    requestAnimationFrame(() => this._syncPlanPreviewSize());
    this._syncWalkHint("fp");
  }

  /** Match the 2D-render box to the minimap canvas's rendered size. */
  _syncPlanPreviewSize() {
    if (!this.planPreviewBox || !this.minimapCanvas) return;
    const r = this.minimapCanvas.getBoundingClientRect();
    if (r.width < 2 || r.height < 2) return; // minimap hidden / not laid out — keep CSS size
    this.planPreviewBox.style.width = `${Math.round(r.width)}px`;
    this.planPreviewBox.style.height = `${Math.round(r.height)}px`;
  }

  /** State machine for the 2D-render frame: placeholder | loading | image. */
  _showPlanState(state) {
    this.planPreviewPlaceholder?.classList.toggle("hidden", state !== "empty");
    this.planPreviewLoading?.classList.toggle("hidden", state !== "loading");
    const keepImg = Boolean(this.planPreviewImg?.getAttribute("src") || this.planPreviewImg?.src);
    this.planPreviewImg?.classList.toggle("hidden", state !== "image" && !(state === "loading" && keepImg));
  }

  /** Data URL currently shown in the 2D Render HUD panel (uploaded render). */
  getPlanImageDataUrl() {
    const src = this.planPreviewImg?.src ?? "";
    if (!src) return null;
    if (src.startsWith("data:") || src.startsWith("blob:")) return src;
    try {
      const u = new URL(src, window.location.href);
      if (u.protocol === "http:" || u.protocol === "https:") return src;
    } catch {
      /* ignore */
    }
    return null;
  }

  /** Show the uploaded 2D render next to the minimap (persists across refresh). */
  _setPlanPreview(dataUrl) {
    if (!this.planPreviewImg || !dataUrl) return;
    this._userPlanImage = true;
    this.planPreviewImg.src = dataUrl;
    this._showPlanState("image");
    this.planOverlayToggle?.classList.remove("hidden");
    if (!this.sandbox) this.planEditBtn?.classList.remove("hidden");
  }

  /** Load the real uploaded 2D render from the orchestrator (not the schematic map). */
  async _syncServerPlanImage() {
    if (this.sandbox || this._userPlanImage || this._planUploading) return;
    const image = await fetchActivePlanImage();
    if (image) {
      this._setPlanPreview(image);
      try {
        const thumb = await compressImageDataUrl(image, 480, 0.7);
        localStorage.setItem(PLAN_IMAGE_KEY, thumb);
      } catch {
        /* preview still shows this session */
      }
    } else if (!this.planPreviewImg?.getAttribute("src")) {
      this._showPlanState("empty");
    }
  }

  _clearPlanPreview() {
    this._planUploading = false;
    this._userPlanImage = false;
    if (this.planPreviewImg) this.planPreviewImg.removeAttribute("src");
    this._showPlanState("empty");
    this._planOverlayOn = false;
    this.planOverlayToggle?.classList.add("hidden");
    this.planOverlayToggle?.classList.remove("hud-chip--accent");
    this.planEditBtn?.classList.add("hidden");
    this.planOverlayOpacity?.classList.add("hidden");
    this.app.fire("ui:plan-overlay", { enabled: false });
    try {
      localStorage.removeItem(PLAN_IMAGE_KEY);
    } catch {
      /* ignore */
    }
  }

  _restorePlanPreview() {
    let saved = null;
    try {
      saved = localStorage.getItem(PLAN_IMAGE_KEY);
    } catch {
      /* ignore */
    }
    if (saved) this._setPlanPreview(saved);
    else this._showPlanState("empty");
  }

  _setTouchPlanStatus(text, { error = false } = {}) {
    if (!this.touchPlanStatusEl) return;
    const value = String(text ?? "");
    this.touchPlanStatusEl.textContent = value;
    this.touchPlanStatusEl.classList.toggle("hidden", !value);
    this.touchPlanStatusEl.classList.toggle("touch-plan-bar__status--error", Boolean(error) && Boolean(value));
  }

  _setAnalysisSummary(text, { error = false } = {}) {
    if (!this.analysisSummaryEl) return;
    const value = String(text ?? "");
    this.analysisSummaryEl.textContent = value;
    this.analysisSummaryEl.classList.toggle("hidden", !value);
    this.analysisSummaryEl.classList.toggle("text-rose-300", error);
    this._setTouchPlanStatus(value, { error });
  }

  /** Clear the custom apartment panel (default home or before a new 2D analysis). */
  _clearHomePanel({ analyzing = false } = {}) {
    this.spec = null;
    this.baselineSpec = null;
    this.analysisBodyEl?.classList.add("hidden");
    if (this.analysisDimsEl) this.analysisDimsEl.innerHTML = "";
    if (this.analysisRoomsEl) {
      this.analysisRoomsEl.classList.add("hidden");
      this.analysisRoomsEl.innerHTML = "";
    }
    if (this.analysisScaleEl) {
      this.analysisScaleEl.classList.add("hidden");
      this.analysisScaleEl.innerHTML = "";
    }
    this._scaleMpp = null;
    this._scaleRef = null;
    if (this.analysisOpeningsEl) this.analysisOpeningsEl.innerHTML = "";
    if (this.analysisConfidenceEl) this.analysisConfidenceEl.textContent = "";
    if (this.analysisWarningEl) {
      this.analysisWarningEl.classList.add("hidden");
      this.analysisWarningEl.textContent = "";
    }
    if (this.analysisResetBtn) this.analysisResetBtn.disabled = true;

    if (analyzing) {
      this.analysisEl?.classList.remove("hidden");
      this._setAnalysisSummary("Replacing home from 2D render…");
      this.analysisSpinnerEl?.classList.remove("hidden");
    } else {
      this.analysisEl?.classList.add("hidden");
      this._setAnalysisSummary("");
      this.analysisSpinnerEl?.classList.add("hidden");
    }
  }

  /** Show the last analyzed apartment in the panel immediately after a refresh. */
  _restoreSavedPlan() {
    const saved = loadSavedPlan();
    if (!saved?.spec) return;
    this._applyAnalysisResult(saved);
  }

  _applyAnalysisResult(result) {
    if (!result?.spec) return;
    if (this.analysisEl) this.analysisEl.classList.remove("hidden");
    this._analysisCollapsed = false;
    this.analysisEl?.classList.remove("analysis-panel--collapsed");
    const summary = result.summary ?? "Apartment rebuilt from the 2D render.";
    const stagedN = Number(result.staged?.placed) || 0;
    this._setAnalysisSummary(
      stagedN > 0 ? `${summary} Staged ${stagedN} catalog pieces.` : summary,
    );
    if (this.analysisConfidenceEl) {
      const c = result.confidence ? String(result.confidence).replace(/\s*est\.?$/i, "") : "";
      this.analysisConfidenceEl.textContent = c ? `⚡ ${c} CONFIDENCE` : "";
      this.analysisConfidenceEl.classList.toggle("hidden", !c);
    }
    this.spec = this._cloneSpec(result.spec);
    if (result.source === "image" || !this.baselineSpec) {
      this.baselineSpec = this._cloneSpec(result.spec);
    }
    this._scaleMpp = Number(result.metersPerPixel ?? result.spec?.graph?.metersPerPixel);
    this._scaleRef = result.scaleReference ?? result.spec?.graph?.scaleReference ?? null;
    this._renderWalkability(result.walkability);
    this._renderForm();
  }

  /**
   * Surface the server's walkability gate: when a built/edited plan seals a room
   * off with no doorway, warn the user (and name the room) instead of letting
   * them walk into an invisible wall. Hidden when every room is reachable.
   */
  _renderWalkability(walkability) {
    const el = this.analysisWarningEl;
    if (!el) return;
    if (!walkability || walkability.ok || !walkability.message) {
      el.classList.add("hidden");
      el.textContent = "";
      return;
    }
    const rooms = Array.isArray(walkability.unreachable) ? walkability.unreachable.filter(Boolean) : [];
    const detail = rooms.length
      ? `${rooms.length === 1 ? "This room is" : "These rooms are"} sealed off (no door): ${rooms.join(", ")}. Open the Plan Editor and add a doorway so you can walk in.`
      : `${walkability.message}. Open the Plan Editor to add a doorway.`;
    el.innerHTML = `<span aria-hidden="true">⚠</span><span>${detail}</span>`;
    el.classList.remove("hidden");
  }

  _initFloorPickers() {
    if (!this.floorBtn || !this.floorMenu) return;

    this._floorOpen = false;

    this.floorBtn.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      this._toggleFloorMenu();
    });

    this.floorMenu.addEventListener("click", (e) => {
      const activeBtn = e.target.closest("[data-floor-active]");
      const countBtn = e.target.closest("[data-floor-count]");
      if (activeBtn && !activeBtn.disabled) {
        e.preventDefault();
        e.stopPropagation();
        this._onFloorActiveChange(Number(activeBtn.dataset.floorActive), false);
      } else if (countBtn) {
        e.preventDefault();
        e.stopPropagation();
        this._onFloorCountChange(Number(countBtn.dataset.floorCount), false);
      }
    });

    document.addEventListener("click", (e) => {
      if (this._floorOpen && !this.floorPickerRoot?.contains(e.target)) this._closeFloorMenu();
    });

    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") this._closeFloorMenu();
    });

    this.app.on("ui:tour-menu", ({ open }) => {
      if (open) this._closeFloorMenu();
    });

    this._renderFloors();
  }

  _toggleFloorMenu() {
    if (this._floorOpen) this._closeFloorMenu();
    else this._openFloorMenu();
  }

  _openFloorMenu() {
    this._buildFloorPanel();
    this._floorOpen = true;
    this.floorMenu?.classList.add("hud-floor__panel--open");
    this.floorMenu?.setAttribute("aria-hidden", "false");
    this.floorBtn?.setAttribute("aria-expanded", "true");
    this.app.fire("ui:floor-menu", { open: true });
  }

  _closeFloorMenu() {
    if (!this._floorOpen) return;
    this._floorOpen = false;
    this.floorMenu?.classList.remove("hud-floor__panel--open");
    this.floorMenu?.setAttribute("aria-hidden", "true");
    this.floorBtn?.setAttribute("aria-expanded", "false");
    this.app.fire("ui:floor-menu", { open: false });
  }

  _fillFloorGridHtml(row, { kind, from, to, value, disabledAbove }) {
    if (!row) return;
    const parts = [];
    for (let i = from; i <= to; i++) {
      const active = i === value;
      const disabled = disabledAbove != null && i > disabledAbove;
      const attr = kind === "active" ? "data-floor-active" : "data-floor-count";
      parts.push(
        `<button type="button" role="option" class="hud-floor__grid-btn${active ? " hud-floor__grid-btn--active" : ""}${disabled ? " hud-floor__grid-btn--disabled" : ""}" ` +
          `${attr}="${i}" aria-selected="${active ? "true" : "false"}"${disabled ? " disabled" : ""}>${i}</button>`,
      );
    }
    row.innerHTML = parts.join("");
  }

  _buildFloorPanel() {
    const root = this.floorPickerRoot ?? this.root;
    const activeRow = root?.querySelector('[data-overlay="floor-active-row"]') ?? this.floorActiveRow;
    const countRow = root?.querySelector('[data-overlay="floor-count-row"]') ?? this.floorCountRow;
    this.floorActiveRow = activeRow;
    this.floorCountRow = countRow;

    const { floorCount, activeFloor } = this._normFloors();
    this._fillFloorGridHtml(activeRow, {
      kind: "active",
      from: 1,
      to: 10,
      value: activeFloor,
      disabledAbove: floorCount,
    });
    this._fillFloorGridHtml(countRow, {
      kind: "count",
      from: 1,
      to: 10,
      value: floorCount,
    });
  }

  _initProvider() {
    if (!this.providerPicker || !this.providerBtn || !this.providerMenu) return;

    this._providerOpen = false;
    this._buildProviderMenu();
    this._setProvider(getAiProvider(), false);
    this._refreshProviderAvailability();

    this.providerBtn.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      this._toggleProviderMenu();
    });

    document.addEventListener("click", (e) => {
      if (!this._providerOpen) return;
      if (this.providerPicker.contains(e.target)) return;
      this._closeProviderMenu();
    });

    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") this._closeProviderMenu();
    });
  }

  _buildProviderMenu() {
    this.providerMenu.innerHTML = "";
    for (const opt of PROVIDER_OPTIONS) {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.role = "option";
      btn.dataset.provider = opt.id;
      btn.className = "provider-picker__option";
      btn.innerHTML =
        `<span class="provider-picker__option-label">${opt.label}</span>` +
        `<span class="provider-picker__option-hint">${opt.hint}</span>` +
        (opt.id === "demo" ? `<span class="provider-picker__option-badge">Free</span>` : "");
      btn.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (btn.disabled) return;
        this._setProvider(opt.id);
        this._closeProviderMenu();
      });
      this.providerMenu.appendChild(btn);
    }
  }

  _setProvider(id, persist = true) {
    if (!PROVIDER_OPTIONS.some((o) => o.id === id)) return;
    if (persist) setAiProvider(id);
    const opt = PROVIDER_OPTIONS.find((o) => o.id === id);
    if (this.providerLabel && opt) this.providerLabel.textContent = opt.label;
    for (const el of this.providerMenu.querySelectorAll("[data-provider]")) {
      const active = el.dataset.provider === id;
      el.classList.toggle("provider-picker__option--active", active);
      el.setAttribute("aria-selected", active ? "true" : "false");
    }
  }

  _toggleProviderMenu() {
    if (this._providerOpen) this._closeProviderMenu();
    else this._openProviderMenu();
  }

  _openProviderMenu() {
    this._refreshProviderAvailability();
    this._providerOpen = true;
    this.providerMenu.classList.remove("hidden");
    this.providerBtn.setAttribute("aria-expanded", "true");
  }

  _closeProviderMenu() {
    this._providerOpen = false;
    this.providerMenu.classList.add("hidden");
    this.providerBtn.setAttribute("aria-expanded", "false");
  }

  async _refreshProviderAvailability() {
    if (!this.providerMenu) return;
    const url = orchUrl();
    try {
      const res = await fetch(`${url}/health`);
      if (!res.ok) return;
      const data = await res.json();
      const p = data.providers ?? {};
      for (const el of this.providerMenu.querySelectorAll("[data-provider]")) {
        const id = el.dataset.provider;
        if (id === "anthropic" && "anthropic" in p) el.disabled = !p.anthropic;
        if (id === "openai" && "openai" in p) el.disabled = !p.openai;
        if ((id === "astra" || id === "astra-pro") && ("openrouter" in p || "astra" in p)) {
          el.disabled = !p.openrouter && !p.astra;
        }
      }
    } catch {
      /* orchestrator offline — leave options enabled */
    }
  }

  async _adminSaveLayout() {
    if (this.demo) return;
    const url = orchUrl();
    const btn = this.adminSaveBtn;
    if (btn) btn.disabled = true;
    this._setAdminSaveHint("Saving…");
    try {
      const placements = await new Promise((resolve) => {
        const t = setTimeout(() => resolve(null), 800);
        this.app.once("ui:admin-save-placements", (list) => {
          clearTimeout(t);
          resolve(Array.isArray(list) ? list : null);
        });
        this.app.fire("ui:admin-save-collect", {});
      });
      const body = {};
      // Only send when collect succeeded — an empty fallback would wipe the world.
      if (Array.isArray(placements)) body.placements = placements;
      const res = await fetch(`${url}/admin/save-layout`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(body),
        signal: AbortSignal.timeout(15_000),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      this._setAdminSaveHint(`Saved ${data.count ?? 0} items globally — Reset restores this for everyone`, false);
      if (btn) {
        btn.classList.add("ring-2", "ring-emerald-400");
        setTimeout(() => btn.classList.remove("ring-2", "ring-emerald-400"), 2000);
      }
    } catch (err) {
      const msg = /Failed to fetch|timed out/i.test(err.message)
        ? "Orchestrator offline — start it on :3000"
        : err.message || "Save failed";
      this._setAdminSaveHint(msg, true);
      if (btn) {
        btn.classList.add("ring-2", "ring-red-400");
        setTimeout(() => btn.classList.remove("ring-2", "ring-red-400"), 2500);
      }
    } finally {
      if (btn) btn.disabled = false;
    }
  }

  _setAdminSaveHint(text, error = false) {
    if (!this.adminSaveHint) return;
    clearTimeout(this._adminSaveHintTimer);
    if (!text) {
      this.adminSaveHint.classList.add("hidden");
      this.adminSaveHint.textContent = "";
      return;
    }
    this.adminSaveHint.textContent = text;
    this.adminSaveHint.classList.remove("hidden");
    this.adminSaveHint.classList.toggle("text-rose-300", Boolean(error));
    this.adminSaveHint.classList.toggle("text-emerald-200", !error);
    this._adminSaveHintTimer = setTimeout(() => {
      this.adminSaveHint?.classList.add("hidden");
    }, error ? 6000 : 4500);
  }

  _bindTimeOfDay() {
    if (!this.timeSlider) return;

    const onSlide = () => {
      const hour = Number(this.timeSlider.value);
      if (this.timeLabel) this.timeLabel.textContent = this._formatHourLabel(hour);
      this._updateTimeSliderStyle(hour);
      this.app.fire("ui:time-of-day", { hour });
    };
    this.timeSlider.addEventListener("input", onSlide);
    this.timeSlider.addEventListener("change", onSlide);

    this.app.on("time:changed", ({ hour, label }) => {
      if (this.timeLabel && label) this.timeLabel.textContent = label;
      if (this.timeSlider && Math.abs(Number(this.timeSlider.value) - hour) > 0.03) {
        this.timeSlider.value = String(hour);
      }
      this._updateTimeSliderStyle(hour);
    });

    // Overlay mounts after DayNightCycle, so the boot `time:changed` is often
    // missed. Force the slider/label AND re-broadcast so PC and mobile both
    // lock to 11:00 (not a leftover 16:15 from a stale hour).
    this.timeSlider.value = String(DEFAULT_INITIAL_HOUR);
    if (this.timeLabel) this.timeLabel.textContent = this._formatHourLabel(DEFAULT_INITIAL_HOUR);
    this._updateTimeSliderStyle(DEFAULT_INITIAL_HOUR);
    this.app.fire("ui:time-of-day", { hour: DEFAULT_INITIAL_HOUR });
  }

  /** Tint slider thumb + readout from hour (dark blue at 0/24, bright at noon). */
  _updateTimeSliderStyle(hour) {
    const panel = this.timeOfDayPanel;
    if (!panel) return;
    const h = ((hour % 24) + 24) % 24;
    const daylight = (Math.cos(((h - 12) / 12) * Math.PI) + 1) / 2;
    const r = Math.round(56 + (255 - 56) * daylight);
    const g = Math.round(92 + (247 - 92) * daylight);
    const b = Math.round(168 + (180 - 168) * daylight);
    const glow = 0.25 + daylight * 0.55;
    panel.style.setProperty("--time-daylight", String(daylight));
    panel.style.setProperty("--thumb-bg", `rgb(${r}, ${g}, ${b})`);
    panel.style.setProperty("--thumb-glow", `rgba(${r}, ${g}, ${b}, ${glow})`);
  }

  _formatHourLabel(hour) {
    const h = ((hour % 24) + 24) % 24;
    const hh = Math.floor(h);
    const mm = Math.round((h - hh) * 60) % 60;
    return `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
  }

  _bind() {
    this.app.on("scene:catalog-loaded", (items) => this.renderCatalog(items));
    this.app.on("ui:budget-changed", ({ total, saleTotal, count, room }) => {
      this.placementCount = count ?? 0;
      this.setBudget(total, count, saleTotal);
      this.setSavings(total, saleTotal, count);
      this._setStatusRoom(room ?? null);
      this._updateHint();
    });
    this.app.on("ui:room-savings", ({ rows }) => {
      this._roomSavingsRows = Array.isArray(rows) ? rows : [];
      this._renderRoomSavings();
    });
    this.app.on("scene:selected", ({ id, name }) => this.setSelected(id, name));

    // Sit/lay interaction: key hint on desktop, tap button on touch UIs.
    const touchUi = document.body.classList.contains("touch-ui");
    this._sitSeated = false;
    const syncSitUi = (candidate) => {
      const verb = candidate?.verb === "lay" ? "lay down" : "sit";
      if (this._sitSeated) {
        if (this.sitHintEl) {
          this.sitHintEl.textContent = "Press E to stand up";
          this.sitHintEl.classList.toggle("hidden", touchUi);
        }
        if (this.interactBtn) {
          this.interactBtn.textContent = "Stand";
          this.interactBtn.classList.toggle("hidden", !touchUi);
        }
        return;
      }
      if (!candidate) {
        this.sitHintEl?.classList.add("hidden");
        this.interactBtn?.classList.add("hidden");
        return;
      }
      if (this.sitHintEl) {
        const name = candidate.name ? ` · ${candidate.name}` : "";
        this.sitHintEl.textContent = `Press E to ${verb}${name}`;
        this.sitHintEl.classList.toggle("hidden", touchUi);
      }
      if (this.interactBtn) {
        this.interactBtn.textContent = candidate.verb === "lay" ? "Lay down" : "Sit";
        this.interactBtn.classList.toggle("hidden", !touchUi);
      }
    };
    this.app.on("sit:candidate", (cand) => {
      this._sitCandidate = cand ?? null;
      syncSitUi(this._sitCandidate);
    });
    this.app.on("sit:state", ({ seated }) => {
      this._sitSeated = Boolean(seated);
      syncSitUi(this._sitCandidate);
    });
    this.interactBtn?.addEventListener("click", () => this.app.fire("ui:interact", {}));
    this.app.on("ui:placement-started", ({ asset_id, name }) => {
      this._placingAsset = asset_id;
      this.catalogPanel?.classList.add("catalog-panel--placing");
      if (this.hintEl) {
        this.hintEl.textContent = `Placing ${name ?? "item"} — click once in the room to place · Q/E rotate · Esc cancel`;
        this.hintEl.classList.remove("hidden");
        this.hintEl.classList.add("text-sky-300");
      }
      this._renderCatalogItems();
      this._renderCatalogCategories();
    });
    this.app.on("ui:placement-ended", () => {
      this._placingAsset = null;
      this.catalogPanel?.classList.remove("catalog-panel--placing");
      this._renderCatalogItems();
      this._renderCatalogCategories();
      this._updateHint();
    });
    this.app.on("ui:placement-hint", ({ text, error }) => {
      if (!this.hintEl || !text) return;
      this.hintEl.textContent = text;
      this.hintEl.classList.toggle("text-rose-300", Boolean(error));
      this.hintEl.classList.toggle("text-sky-300", !error && this._placingAsset);
    });
    this.app.on("scene:hover-info", (e) => this._onHoverInfo(e));
    this.app.on("fp:lock-changed", ({ locked }) => {
      this._fpLocked = Boolean(locked);
      if (
        !locked &&
        this._camMode !== "overhead" &&
        !document.body.classList.contains("touch-ui")
      ) {
        this._hideHoverCard();
      }
    });
    this.app.on("cam:mode-changed", ({ mode }) => {
      this._camMode = mode ?? "fp";
      if (mode === "overhead") return;
      if (!this._fpLocked && !document.body.classList.contains("touch-ui")) {
        this._hideHoverCard();
      }
    });
    this.hoverBuy?.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      this._openBuySoon();
    });
    this.buySoonBack?.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      this._closeBuySoon();
    });
    this.buySoon?.addEventListener("click", (e) => {
      if (e.target === this.buySoon) this._closeBuySoon();
    });
    this.app.on("ui:home-reset", (e) => {
      this._clearHomePanel(e ?? {});
      // Keep the in-flight 2D preview while a new plan is being analyzed.
      if (e && e.analyzing) return;
      this._planUploading = false;
      this._pendingPlanThumb = null;
      this._clearPlanPreview();
    });
    this.app.on("ui:reset-failed", ({ text }) => {
      if (!this.homeResetBtn) return;
      this.homeResetBtn.title = text ?? "Reset failed";
      this.homeResetBtn.classList.add("ring-2", "ring-red-400");
      setTimeout(() => this.homeResetBtn?.classList.remove("ring-2", "ring-red-400"), 2500);
    });
  }

  _applyHoverCardPos() {
    if (!this.hoverCard) return;
    this.hoverCard.style.transform = `translate3d(${this._hoverPos.x}px, ${this._hoverPos.y}px, 0)`;
  }

  _tickHoverCard(dt) {
    if (document.body.classList.contains("revive-open")) return;
    if (!this.hoverCard || !this._hoverTarget) return;
    const frameDt = Math.min(0.05, Math.max(0.001, dt ?? 0.016));
    const tx = this._hoverTarget.x;
    const ty = this._hoverTarget.y;
    const touchUi = document.body.classList.contains("touch-ui");

    if (this._hoverSlide) {
      this._hoverSlide.t += frameDt;
      const u = hoverEaseOut(this._hoverSlide.t / this._hoverSlide.dur);
      this._hoverPos.x = this._hoverSlide.x0 + (tx - this._hoverSlide.x0) * u;
      this._hoverPos.y = this._hoverSlide.y0 + (ty - this._hoverSlide.y0) * u;
      if (this._hoverSlide.t >= this._hoverSlide.dur) {
        this._hoverPos.x = tx;
        this._hoverPos.y = ty;
        this._hoverSlide = null;
      }
    } else {
      // Same object: stay glued so walk / gyro don't rubber-band.
      const rate = touchUi ? 18 : 16;
      const k = 1 - Math.exp(-rate * frameDt);
      this._hoverPos.x += (tx - this._hoverPos.x) * k;
      this._hoverPos.y += (ty - this._hoverPos.y) * k;
    }
    this._applyHoverCardPos();
  }

  _startHoverSlide(x, y) {
    const dist = Math.hypot(x - this._hoverPos.x, y - this._hoverPos.y);
    if (dist < 28) {
      this._hoverSlide = null;
      return;
    }
    const touchUi = document.body.classList.contains("touch-ui");
    this._hoverSlide = {
      x0: this._hoverPos.x,
      y0: this._hoverPos.y,
      t: 0,
      dur: touchUi ? 0.48 : 0.36,
    };
  }

  _isPhotorealHover() {
    return Boolean(document.querySelector(".revive-layer--photoreal"));
  }

  _finishHoverHide() {
    this.hoverCard?.classList.remove("is-hiding", "hover-card--surface", "hover-card--touch");
    this.hoverCard?.setAttribute("aria-hidden", "true");
    this._hoverShown = false;
    this._hoverTarget = null;
    this._hoverLastId = null;
    this._hoverSlide = null;
    this._hoverFlip = false;
    const pending = this._pendingHover;
    this._pendingHover = null;
    if (pending?.info && pending.screen) this._onHoverInfo(pending);
  }

  _hideHoverCard() {
    if (!this.hoverCard) return;
    clearTimeout(this._hoverHideTimer);
    clearTimeout(this._hoverNullTimer);
    if (!this._hoverShown) {
      this.hoverCard.classList.remove("is-visible", "is-hiding", "hover-card--surface", "hover-card--touch");
      this.hoverCard.setAttribute("aria-hidden", "true");
      this._hoverTarget = null;
      this._hoverLastId = null;
      this._hoverSlide = null;
      this._hoverFlip = false;
      return;
    }
    this.hoverCard.classList.remove("is-visible");
    this.hoverCard.classList.add("is-hiding");
    this._hoverHideTimer = setTimeout(() => {
      this._hoverHideTimer = null;
      this._finishHoverHide();
    }, this._isPhotorealHover() ? 280 : 340);
  }

  /** Park the tour price card above Revive so it is not trapped under #overlay. */
  _syncHoverCardHost() {
    if (!this.hoverCard || !this.root) return;
    const revive = document.body.classList.contains("revive-open");
    const host = revive ? document.body : this.root;
    if (this.hoverCard.parentElement !== host) host.appendChild(this.hoverCard);
    if (this.buySoon && this.buySoon.parentElement !== host) host.appendChild(this.buySoon);
  }

  /** Walk mode shows price cards while pointer-locked; phones aim via center ring. */
  _hoverPriceVisible() {
    if (document.body.classList.contains("revive-open")) return true;
    if (!this._hoverUiOn) return false;
    if (document.body.classList.contains("cinematic-reel")) return true;
    if (this._camMode === "overhead") return true;
    if (document.body.classList.contains("plan-editor-open")) return false;
    if (document.body.classList.contains("touch-ui")) return true;
    const canvas = this.app.graphicsDevice?.canvas;
    return Boolean(canvas && document.pointerLockElement === canvas);
  }

  _openBuySoon() {
    if (!this.buySoon) return;
    this.buySoon.classList.remove("hidden");
    this.buySoon.setAttribute("aria-hidden", "false");
    document.body.classList.add("buy-soon-open");
    this.app.fire("ui:buy-soon", { open: true });
  }

  _closeBuySoon() {
    if (!this.buySoon) return;
    this.buySoon.classList.add("hidden");
    this.buySoon.setAttribute("aria-hidden", "true");
    document.body.classList.remove("buy-soon-open");
    this.app.fire("ui:buy-soon", { open: false });
  }

  _onHoverInfo({ info, screen, follow } = {}) {
    if (!this.hoverCard) return;
    this._syncHoverCardHost();

    if (!info || !screen || !this._hoverPriceVisible()) {
      if (!this._hoverShown) return;
      clearTimeout(this._hoverNullTimer);
      // Photoreal: fade off the item immediately. Tour/phones keep a short hold.
      // Keep a queued item — pointerleave of the old hit can land after pointerenter of the next.
      if (this._isPhotorealHover()) {
        if (this._pendingHover) return;
        this._hideHoverCard();
        return;
      }
      const hideDelay = document.body.classList.contains("touch-ui") ? 1200 : 200;
      this._hoverNullTimer = setTimeout(() => {
        this._hoverNullTimer = null;
        this._hideHoverCard();
      }, hideDelay);
      return;
    }

    const itemKeyEarly = `${info.category ?? ""}:${info.id ?? info.name ?? ""}`;
    const photoreal = this._isPhotorealHover();
    const hiding = this.hoverCard.classList.contains("is-hiding");
    const itemChangedEarly = itemKeyEarly !== this._hoverLastId;
    if (photoreal && (this._hoverShown || hiding) && (itemChangedEarly || hiding)) {
      this._pendingHover = { info, screen, follow: false };
      if (!hiding) this._hideHoverCard();
      return;
    }

    clearTimeout(this._hoverNullTimer);
    clearTimeout(this._hoverHideTimer);
    this._hoverHideTimer = null;
    this.hoverCard.classList.remove("is-hiding");
    this.hoverCard.setAttribute("aria-hidden", "false");
    if (!this.hoverCard.classList.contains("is-visible")) {
      this.hoverCard.classList.add("is-visible");
    }

    const touchUi = document.body.classList.contains("touch-ui");
    const surfaceHover = info.category === "floor" || info.category === "ceiling";

    // Track the object's screen anchor (same fluid follow as desktop).
    // On phones, keep a tighter inset so the compact card stays on-screen.
    const padX = touchUi ? 10 : 8;
    const padY = touchUi ? 72 : 96;
    let x = Math.max(padX, Math.min(window.innerWidth - padX, screen.x));
    let y = Math.max(padY, Math.min(window.innerHeight - padY, screen.y));

    // Belt-and-suspenders: never leave floor/ceiling cards on the screen-center reticle.
    if (surfaceHover) {
      const cx = window.innerWidth / 2;
      const cy = window.innerHeight / 2;
      if (Math.hypot(x - cx, y - cy) < (touchUi ? 100 : 140)) {
        const preferRight = cx < window.innerWidth * 0.55;
        const nudge = touchUi ? 110 : 200;
        x = cx + (preferRight ? nudge : -nudge);
        y = cy + (info.category === "ceiling" ? 40 : -40);
        x = Math.max(padX, Math.min(window.innerWidth - padX, x));
        y = Math.max(padY, Math.min(window.innerHeight - padY, y));
      }
    }

    const flipOn = touchUi ? window.innerWidth - 150 : window.innerWidth - 360;
    const flipOff = touchUi ? window.innerWidth - 240 : window.innerWidth - 420;
    if (this._hoverFlip) {
      if (x < flipOff) this._hoverFlip = false;
    } else if (x > flipOn) {
      this._hoverFlip = true;
    }

    // First show: snap. Item-to-item: ease the card across the screen.
    // Revive has no PlayCanvas prerender tick (2D layer covers the canvas),
    // so a slide would freeze at the last item — always snap to the pointer.
    const itemKey = `${info.category ?? ""}:${info.id ?? info.name ?? ""}`;
    const itemChanged = itemKey !== this._hoverLastId;
    const revive = document.body.classList.contains("revive-open");
    if (revive || !this._hoverShown) {
      this._hoverPos.x = x;
      this._hoverPos.y = y;
      this._hoverSlide = null;
      this._applyHoverCardPos();
    } else if (itemChanged) {
      this._startHoverSlide(x, y);
    }
    this._hoverTarget = { x, y };
    this.hoverCard.classList.toggle("flip", this._hoverFlip);
    this.hoverCard.classList.toggle("hover-card--touch", touchUi);
    // Floor/ceiling share the view with the center reticle — hide the locator
    // orb so the card sits beside the crosshair instead of stacking on it.
    this.hoverCard.classList.toggle("hover-card--surface", surfaceHover);

    this._hoverLastId = itemKey;

    // Follow ticks only update the screen anchor — skip DOM thrash.
    if ((follow || !itemChanged) && this._hoverShown) {
      return;
    }

    if (this.hoverName) this.hoverName.textContent = info.name ?? "Item";

    // Aspect marketplace: partner house + deterministic 10–30% sale on every object.
    const saleKey = info.id ?? info.name ?? info.category ?? "item";
    const partner = partnerBrandForId(saleKey);
    if (this.hoverSub) {
      this.hoverSub.textContent = `Aspect · ${partner}`;
    }

    const sale = aspectSalePrice(info.price, saleKey);
    if (this.hoverPrice) {
      const next = sale ? formatGelPrice(sale.sale) : info.price != null ? formatGelPrice(info.price) : "—";
      if (this.hoverPrice.textContent !== next) {
        this.hoverPrice.textContent = next;
        if (this._hoverShown && itemChanged) {
          this.hoverPrice.classList.remove("is-updating");
          void this.hoverPrice.offsetWidth;
          this.hoverPrice.classList.add("is-updating");
        }
      }
      this.hoverPrice.classList.toggle("hover-panel__price-value--sale", Boolean(sale));
    }
    if (this.hoverPriceOld) {
      this.hoverPriceOld.textContent = sale ? formatGelPrice(sale.list) : "";
    }
    if (this.hoverSaleBadge) {
      this.hoverSaleBadge.textContent = sale ? `−${sale.pct}%` : "";
    }

    if (this.hoverDims) {
      this.hoverDims.textContent = info.dims ?? "";
      this.hoverDims.classList.toggle("is-empty", !info.dims);
    }

    if (!this._hoverShown) {
      requestAnimationFrame(() => {
        this.hoverCard?.classList.add("is-visible");
      });
    }
    this._hoverShown = true;
  }

  _bindPrompt() {
    if (!this.promptForm) return;

    this.promptForm.addEventListener("submit", (e) => {
      e.preventDefault();
      const text = (this.promptInput?.value ?? "").trim();
      if (!text) return;
      // Named finishes/themes restyle the home even if a wall is selected.
      // Geometry refine stays for "move this door" style instructions.
      const restyle = parseRestyleIntent(text);
      if (this._structureSel.length && !restyle.matched) {
        this.app.fire("ui:refine-structure", {
          instruction: text,
          elements: this._structureSel.map((s) => ({ id: s.id, kind: s.kind })),
        });
      } else {
        this.app.fire("ui:prompt", { prompt: text });
      }
      if (this.promptInput) this.promptInput.value = "";
    });

    // Keystrokes in the field must not leak into the PlayCanvas keyboard
    // (otherwise typing W/A/S/D/Q/E would move the camera or rotate furniture).
    for (const ev of ["keydown", "keyup", "keypress"]) {
      this.promptInput?.addEventListener(ev, (e) => e.stopPropagation());
    }

    this.app.on("ai:status", ({ text, busy, error }) => this._setStatus(text, busy, error));

    // Attach a 2D render -> read as data URL -> hand off to the shell to analyze.
    const pickPlan = () => this.imageInput?.click();
    this.attachBtn?.addEventListener("click", pickPlan);
    this.touchUploadBtn?.addEventListener("click", pickPlan);
    this.planPreviewBox?.addEventListener("click", pickPlan);
    this.imageInput?.addEventListener("change", () => {
      const file = this.imageInput.files?.[0];
      if (!file) return;
      this._planUploading = true;
      this._showPlanState("loading");
      const reader = new FileReader();
      reader.onload = async () => {
        const dataUrl = reader.result;
        this._setPlanPreview(dataUrl);
        this.app.fire("ui:image", { dataUrl, name: file.name });
        this.imageInput.value = ""; // allow re-selecting the same file

        // Compress to a small thumbnail so it survives a refresh without busting
        // storage. Stash it; it's revealed once analysis finishes.
        try {
          const thumb = await compressImageDataUrl(dataUrl, 480, 0.7);
          this._pendingPlanThumb = thumb;
          try {
            localStorage.setItem(PLAN_IMAGE_KEY, thumb);
          } catch {
            /* storage full / unavailable — preview still shows this session */
          }
        } catch {
          this._pendingPlanThumb = dataUrl;
        }
      };
      reader.readAsDataURL(file);
    });

    this.app.on("ai:analysis", (e) => this._onAnalysis(e));

    const resetHome = () => {
      this.app.fire("ui:reset-home", {});
      this._clearPlanPreview();
      this._setTouchPlanStatus("");
    };
    this.homeResetBtn?.addEventListener("click", resetHome);
    this.touchResetBtn?.addEventListener("click", resetHome);

    if (!this.demo && !this.sandbox) {
      this.adminSaveBtn?.addEventListener("click", () => {
        void this._adminSaveLayout();
      });
    } else {
      this.adminSaveBtn?.remove();
      this.adminSaveHint?.remove();
      this.adminSaveBtn = null;
      this.adminSaveHint = null;
    }

    this.camToggleBtn?.addEventListener("click", () => this.app.fire("ui:toggle-camera", {}));

    this.modeWalkBtn?.addEventListener("click", () => this.app.fire("ui:set-view-mode", { mode: "walk" }));
    this.modePlanBtn?.addEventListener("click", () => this.app.fire("ui:set-view-mode", { mode: "plan" }));
    this.modeOverheadBtn?.addEventListener("click", () => this.app.fire("ui:set-view-mode", { mode: "overhead" }));

    // Object hover-info cards toggle (ON by default; persisted per browser).
    const syncHoverInfoBtn = () => {
      this.hoverInfoToggleBtn?.classList.toggle("mode-segment__btn--active", this._hoverUiOn);
      this.hoverInfoToggleBtn?.setAttribute("aria-pressed", String(this._hoverUiOn));
      if (this.hoverInfoToggleBtn) {
        this.hoverInfoToggleBtn.title = this._hoverUiOn
          ? "Hover info cards: ON — click to hide"
          : "Hover info cards: OFF — click to show";
      }
    };
    this.hoverInfoToggleBtn?.addEventListener("click", () => {
      this._hoverUiOn = !this._hoverUiOn;
      try {
        localStorage.setItem(HOVER_UI_KEY, this._hoverUiOn ? "on" : "off");
      } catch {
        /* private mode */
      }
      if (!this._hoverUiOn) this._hideHoverCard();
      syncHoverInfoBtn();
    });
    syncHoverInfoBtn();

    // Display currency (USD default; Lari optional on desktop).
    if (document.body.classList.contains("touch-ui") || window.matchMedia?.("(pointer: coarse)")?.matches) {
      setCurrency("USD");
    }
    const syncCurrencyBtns = () => {
      const usd = getCurrency() === "USD";
      this.currencyGelBtn?.classList.toggle("mode-segment__btn--active", !usd);
      this.currencyUsdBtn?.classList.toggle("mode-segment__btn--active", usd);
    };
    this.currencyGelBtn?.addEventListener("click", () => setCurrency("GEL"));
    this.currencyUsdBtn?.addEventListener("click", () => setCurrency("USD"));
    window.addEventListener("aspect:currency-changed", () => {
      syncCurrencyBtns();
      // Re-render every price on screen with the new currency.
      this._renderCatalogItems();
      if (this._lastBudget) this.setBudget(this._lastBudget.total, this._lastBudget.count, this._lastBudget.saleTotal);
      if (this._lastSavings) this.setSavings(this._lastSavings.list, this._lastSavings.sale, this._lastSavings.count);
      this._renderRoomSavings();
    });
    syncCurrencyBtns();

    this.analysisToggleBtn?.addEventListener("click", () => {
      this._analysisCollapsed = !this._analysisCollapsed;
      this.analysisEl?.classList.toggle("analysis-panel--collapsed", this._analysisCollapsed);
    });

    this.app.on("plan-editor:opened", () => this._setHudViewMode("plan"));
    this.app.on("plan-editor:closed", () => this._setHudViewMode("walk"));
    this.app.on("cam:mode-changed", ({ mode }) => {
      if (this.camToggleLabel) this.camToggleLabel.textContent = mode === "overhead" ? "First-Person" : "Overhead View";
      if (mode === "overhead") this._setHudViewMode("overhead");
      else if (this._hudViewMode === "overhead") this._setHudViewMode("walk");
      this._syncWalkHint(mode);
    });

    this.app.on("scene:room-loaded", (room) => this._syncFloorPicker(room));

    this.analysisApplyBtn?.addEventListener("click", () => {
      void this._applyAnalysisEdits();
    });
    this.analysisResetBtn?.addEventListener("click", () => {
      if (!this.baselineSpec) return;
      this.spec = this._cloneSpec(this.baselineSpec);
      this._renderForm();
      void this._applyAnalysisEdits();
    });
    this.addDoorBtn?.addEventListener("click", () => this._addOpening("door"));
    this.addWindowBtn?.addEventListener("click", () => this._addOpening("window"));
  }

  /** Toggle/opacity for the registered overlay (rebuilt plan over the 2D image). */
  _bindPlanOverlay() {
    this.planOverlayToggle?.addEventListener("click", () => {
      this._planOverlayOn = !this._planOverlayOn;
      this.planOverlayToggle.classList.toggle("hud-chip--accent", this._planOverlayOn);
      this.planOverlayToggle.classList.remove("bg-aspect-accent", "text-white", "text-white/70");
      this.planOverlayOpacity?.classList.toggle("hidden", !this._planOverlayOn);
      this.app.fire("ui:plan-overlay", {
        enabled: this._planOverlayOn,
        opacity: Number(this.planOverlayOpacity?.value ?? 0.6),
      });
    });
    this.planOverlayOpacity?.addEventListener("input", () => {
      this.app.fire("ui:plan-overlay", { opacity: Number(this.planOverlayOpacity.value) });
    });
    this.planEditBtn?.addEventListener("click", () => this.app.fire("ui:open-plan-editor", {}));
  }

  /** Marquee rectangle + selection chips for overhead structure editing. */
  _bindStructure() {
    this.app.on("structure:rect", (r) => this._drawMarquee(r));
    this.app.on("structure:selection-changed", ({ elements }) => this._renderSelectionChips(elements ?? []));
  }

  _drawMarquee(r) {
    if (!this.marqueeEl) return;
    if (!r?.active) {
      this.marqueeEl.classList.add("hidden");
      return;
    }
    const x = Math.min(r.x0, r.x1);
    const y = Math.min(r.y0, r.y1);
    const w = Math.abs(r.x1 - r.x0);
    const h = Math.abs(r.y1 - r.y0);
    this.marqueeEl.style.left = `${x}px`;
    this.marqueeEl.style.top = `${y}px`;
    this.marqueeEl.style.width = `${w}px`;
    this.marqueeEl.style.height = `${h}px`;
    this.marqueeEl.classList.remove("hidden");
  }

  _setHudViewMode(mode) {
    const m = mode === "plan" || mode === "overhead" ? mode : "walk";
    this._hudViewMode = m;
    this.modeWalkBtn?.classList.toggle("mode-segment__btn--active", m === "walk");
    this.modePlanBtn?.classList.toggle("mode-segment__btn--active", m === "plan");
    this.modeOverheadBtn?.classList.toggle("mode-segment__btn--active", m === "overhead");
    if (m === "walk") this._syncWalkHint("fp");
    else this.walkHintEl?.classList.add("hidden");
  }

  _syncWalkHint(camMode) {
    if (!this.walkHintEl) return;
    const show = camMode === "fp" && this._hudViewMode === "walk" && !document.body.classList.contains("plan-editor-open");
    this.walkHintEl.classList.toggle("hidden", !show);
  }

  _renderSelectionChips(elements) {
    this._structureSel = elements;
    if (!this.selectionChipsEl) return;
    this.selectionChipsEl.innerHTML = "";
    if (!elements.length) {
      this.selectionChipsEl.classList.add("hidden");
      this.selectionChipsEl.classList.remove("flex");
      if (this.promptInput) this.promptInput.placeholder = "Describe the room…  or attach a 2D render to rebuild it";
      return;
    }

    this.selectionChipsEl.classList.remove("hidden");
    this.selectionChipsEl.classList.add("flex");

    const head = document.createElement("span");
    head.className = "mr-0.5 font-semibold uppercase tracking-wide ui-glass-field-label";
    head.textContent = "Editing structure:";
    this.selectionChipsEl.appendChild(head);

    for (const el of elements) {
      const chip = document.createElement("span");
      chip.className = "ui-glass-chip-inline";
      const label = document.createElement("span");
      label.textContent = el.label ?? el.id;
      chip.appendChild(label);
      const x = document.createElement("button");
      x.type = "button";
      x.className = "leading-none opacity-75 hover:opacity-100";
      x.textContent = "✕";
      x.title = "Deselect";
      x.addEventListener("click", () => this.app.fire("structure:deselect", { id: el.id }));
      chip.appendChild(x);
      this.selectionChipsEl.appendChild(chip);
    }

    const clear = document.createElement("button");
    clear.type = "button";
    clear.className = "ui-glass-link ml-auto";
    clear.textContent = "Clear";
    clear.addEventListener("click", () => this.app.fire("structure:clear", {}));
    this.selectionChipsEl.appendChild(clear);

    if (this.promptInput) this.promptInput.placeholder = "Describe the fix for the selected walls/doors…";
  }

  _cloneSpec(spec) {
    const cloneJson = (v) => {
      if (v == null) return v;
      try {
        return structuredClone(v);
      } catch {
        return JSON.parse(JSON.stringify(v));
      }
    };
    return {
      width: spec.width,
      depth: spec.depth,
      height: spec.height,
      floorCount: Math.min(10, Math.max(1, Math.round(Number(spec.floorCount) || DEFAULT_FLOOR_COUNT))),
      activeFloor: Math.min(10, Math.max(1, Math.round(Number(spec.activeFloor) || DEFAULT_ACTIVE_FLOOR))),
      openings: (spec.openings ?? []).map((o) => ({ ...o })),
      ...(spec.rooms ? { rooms: cloneJson(spec.rooms) } : {}),
      ...(spec.graph ? { graph: cloneJson(spec.graph) } : {}),
      ...(spec.semantics ? { semantics: cloneJson(spec.semantics) } : {}),
      ...(spec.semanticsAnchors ? { semanticsAnchors: cloneJson(spec.semanticsAnchors) } : {}),
      ...(spec.layoutMode ? { layoutMode: spec.layoutMode } : {}),
      ...(Array.isArray(spec.suppressedOpenings) ? { suppressedOpenings: [...spec.suppressedOpenings] } : {}),
      ...(Array.isArray(spec.blockedDoorWalls) ? { blockedDoorWalls: [...spec.blockedDoorWalls] } : {}),
      ...(Array.isArray(spec.addedOpenings) ? { addedOpenings: cloneJson(spec.addedOpenings) } : {}),
      ...(Array.isArray(spec.builtOpenings) ? { builtOpenings: cloneJson(spec.builtOpenings) } : {}),
    };
  }

  /** Merge HUD + form edits into the spec sent to the orchestrator on Apply. */
  _prepareSpecForApply() {
    if (!this.spec) return null;
    const spec = this._cloneSpec(this.spec);
    const { floorCount, activeFloor } = this._normFloors(spec);
    spec.floorCount = floorCount;
    spec.activeFloor = activeFloor;
    if (spec.graph) {
      spec.graph.height = Number(spec.height) || spec.graph.height || 2.7;
      if (Number.isFinite(this._scaleMpp) && this._scaleMpp > 0) {
        spec.graph.metersPerPixel = this._scaleMpp;
      }
      if (this._scaleRef) spec.graph.scaleReference = this._scaleRef;
    }
    return spec;
  }

  async _applyAnalysisEdits() {
    const spec = this._prepareSpecForApply();
    if (!spec) {
      this.app.fire("ai:analysis", {
        phase: "error",
        text: "Nothing to apply — upload a 2D floor plan first.",
      });
      return;
    }
    this.analysisEl?.classList.remove("hidden");
    this._analysisCollapsed = false;
    this.analysisEl?.classList.remove("analysis-panel--collapsed");
    this._setAnalysisSummary("Rebuilding the room…");
    this.analysisSpinnerEl?.classList.remove("hidden");
    if (this.sandbox) {
      this.app.fire("scene:sandbox-expect", {});
      this.app.fire("scene:reapply-session", {});
      this._setAnalysisSummary("Room tour updated from the 2D render.");
      this.analysisSpinnerEl?.classList.add("hidden");
      return;
    }
    this.app.fire("ui:rebuild-room", { spec });
  }

  /** End the 2D-render loading state once analysis settles; reveal the image. */
  _finishPlanUpload() {
    if (!this._planUploading) return;
    this._planUploading = false;
    if (this._pendingPlanThumb) {
      this._setPlanPreview(this._pendingPlanThumb);
      this._pendingPlanThumb = null;
    } else {
      this._showPlanState(this.planPreviewImg?.src ? "image" : "empty");
    }
    requestAnimationFrame(() => this._syncPlanPreviewSize());
  }

  _onAnalysis({ phase, text, result }) {
    if (phase === "error" || phase === "done") this._finishPlanUpload();
    if (!this.analysisEl) return;
    this.analysisEl.classList.remove("hidden");

    const busy = phase === "start" || phase === "progress";
    this.analysisSpinnerEl?.classList.toggle("hidden", !busy);
    if (this.attachBtn) this.attachBtn.disabled = busy;
    if (this.touchUploadBtn) this.touchUploadBtn.disabled = busy;
    if (this.analysisApplyBtn) this.analysisApplyBtn.disabled = busy;
    if (this.analysisResetBtn) this.analysisResetBtn.disabled = busy || !this.baselineSpec;

    if (phase === "error") {
      this._setAnalysisSummary(text ?? "Analysis failed.", { error: true });
      this.analysisSpinnerEl?.classList.add("hidden");
      if (this.attachBtn) this.attachBtn.disabled = false;
      if (this.touchUploadBtn) this.touchUploadBtn.disabled = false;
      this._finishPlanUpload();
      return;
    }
    if (phase === "done") {
      this.analysisSpinnerEl?.classList.add("hidden");
      if (this.attachBtn) this.attachBtn.disabled = false;
      if (this.touchUploadBtn) this.touchUploadBtn.disabled = false;
      if (this.analysisApplyBtn) this.analysisApplyBtn.disabled = false;
      if (this.analysisSummaryEl && !this.analysisSummaryEl.classList.contains("text-rose-300")) {
        const t = this.analysisSummaryEl.textContent;
        if (t === "Rebuilding the room…" || t === "Working…") {
          this._setAnalysisSummary("Room updated.");
        }
      }
      this._finishPlanUpload();
      return;
    }
    if (this.analysisSummaryEl) this.analysisSummaryEl.classList.remove("text-rose-300");

    if (busy) {
      this._setAnalysisSummary(text ?? "Working…");
      return;
    }

    if (phase === "result" && result?.spec) {
      saveSavedPlan({
        spec: result.spec,
        summary: result.summary,
        confidence: result.confidence,
        source: result.source,
        walkability: result.walkability,
        ...(result.staged ? { staged: result.staged } : {}),
      });
      this._applyAnalysisResult(result);
    }
  }

  /** Build the editable dimension + openings form from this.spec. */
  _renderForm() {
    if (!this.spec || !this.analysisBodyEl) return;
    this.analysisBodyEl.classList.remove("hidden");
    if (this.analysisResetBtn) this.analysisResetBtn.disabled = !this.baselineSpec;

    // --- Room dimensions ---
    if (this.analysisDimsEl) {
      this.analysisDimsEl.innerHTML = "";
      for (const key of ["width", "depth", "height"]) {
        this.analysisDimsEl.appendChild(this._dimField(key, this.spec[key]));
      }
    }

    this._renderFloors();

    // --- Rooms (read-only breakdown, when a multi-room plan was analyzed) ---
    this._renderRooms();

    // --- Scale calibration (graph plans only) ---
    this._renderScale();

    // --- Openings ---
    if (this.analysisOpeningsEl) {
      this.analysisOpeningsEl.innerHTML = "";
      for (const [i, op] of (this.spec.openings ?? []).entries()) {
        this.analysisOpeningsEl.appendChild(this._openingRow(op, i));
      }
    }

    this._updateFloor();
  }

  _normFloors(spec = this.spec) {
    const fc = Math.min(
      10,
      Math.max(1, Math.round(Number(spec?.floorCount ?? this._floorState?.floorCount) || DEFAULT_FLOOR_COUNT)),
    );
    const af = Math.min(fc, Math.max(1, Math.round(Number(spec?.activeFloor ?? this._floorState?.activeFloor) || DEFAULT_ACTIVE_FLOOR)));
    return { floorCount: fc, activeFloor: af };
  }

  /** Fill the HUD + analysis storey pickers (1–10 floors; active floor 1..N). */
  _renderFloors() {
    const { floorCount, activeFloor } = this._normFloors();
    this._floorState = { floorCount, activeFloor };
    if (this.spec) {
      this.spec.floorCount = floorCount;
      this.spec.activeFloor = activeFloor;
    }
    this.floorPickerEl?.classList.remove("hidden");
    if (this.floorActiveLabel) this.floorActiveLabel.textContent = String(activeFloor);
    if (this.floorCountLabel) this.floorCountLabel.textContent = String(floorCount);
    this._buildFloorPanel();

    if (this.analysisFloorsEl) {
      this.analysisFloorsEl.classList.remove("hidden");
      this.analysisFloorsEl.innerHTML =
        `<div class="ui-glass-field-label mb-1">Building storeys</div>` +
        `<p class="ui-glass-text-muted">` +
        `The active floor shows your full apartment layout. Other floors render as solid wall shells above and below.</p>`;
    }
  }

  _syncFloorPicker(room) {
    if (!room) return;
    this._floorState = {
      floorCount: room.floorCount ?? this._floorState?.floorCount ?? DEFAULT_FLOOR_COUNT,
      activeFloor: room.activeFloor ?? this._floorState?.activeFloor ?? DEFAULT_ACTIVE_FLOOR,
    };
    if (this.spec) {
      this.spec.floorCount = this._floorState.floorCount;
      this.spec.activeFloor = this._floorState.activeFloor;
    }
    this._renderFloors();
  }

  _onFloorActiveChange(value, closeMenu = true) {
    const { floorCount, activeFloor } = this._normFloors();
    const af = Math.min(floorCount, Math.max(1, Math.round(Number(value ?? activeFloor) || 1)));
    this._floorState = { floorCount, activeFloor: af };
    if (this.spec) this.spec.activeFloor = af;
    if (closeMenu) this._closeFloorMenu();
    this._renderFloors();
    this.app.fire("ui:floor-change", { activeFloor: af, floorCount });
  }

  _onFloorCountChange(value, closeMenu = true) {
    const { activeFloor } = this._normFloors();
    const fc = Math.min(10, Math.max(1, Math.round(Number(value ?? this._floorState.floorCount) || 1)));
    const af = Math.min(fc, activeFloor);
    this._floorState = { floorCount: fc, activeFloor: af };
    if (this.spec) {
      this.spec.floorCount = fc;
      this.spec.activeFloor = af;
    }
    if (closeMenu) this._closeFloorMenu();
    this._renderFloors();
    this.app.fire("ui:floor-change", { activeFloor: af, floorCount: fc });
  }

  _renderRooms() {
    if (!this.analysisRoomsEl) return;
    const rooms = this.spec?.rooms ?? [];
    if (rooms.length === 0) {
      this.analysisRoomsEl.classList.add("hidden");
      this.analysisRoomsEl.innerHTML = "";
      return;
    }
    this.analysisRoomsEl.classList.remove("hidden");
    const rows = rooms
      .map((r, i) => {
        const reported = Number(r.reportedArea);
        const area =
          Number.isFinite(reported) && reported > 0
            ? Math.round(reported * 10) / 10
            : r.area
              ? Math.round(r.area * 10) / 10
              : Math.round((Number(r.width) || 0) * (Number(r.depth) || 0) * 10) / 10;
        const label = r.name ?? "Room";
        const type = r.type && r.type !== "other" ? ` · ${r.type}` : "";
        const active = i === 0 ? " analysis-room-row--active" : "";
        return (
          `<div class="analysis-room-row${active}">` +
          `<span class="analysis-room-row__name">${label}${type}</span>` +
          `<span class="analysis-room-row__area">${area} m²</span></div>`
        );
      })
      .join("");
    this.analysisRoomsEl.innerHTML = rows;
  }

  /**
   * Show the detected pixel->meter scale with a multiplier to lock it exactly.
   * Only meaningful for graph-based (vision) plans that carry meters_per_pixel.
   */
  _renderScale() {
    const el = this.analysisScaleEl;
    if (!el) return;
    const mpp = Number(this._scaleMpp);
    if (!(Number.isFinite(mpp) && mpp > 0)) {
      el.classList.add("hidden");
      el.innerHTML = "";
      return;
    }
    el.classList.remove("hidden");
    el.innerHTML = "";

    const head = document.createElement("div");
    head.className = "ui-glass-field-label mb-1";
    head.textContent = "Scale";
    el.appendChild(head);

    const info = document.createElement("div");
    info.className = "ui-glass-text-muted mb-1.5";
    const ref = this._scaleRef?.identified_text;
    info.textContent = `1 px ≈ ${mpp.toFixed(4)} m${ref ? ` (from “${ref}”)` : ""}`;
    el.appendChild(info);

    this._scaleFactor = 1;
    const row = document.createElement("div");
    row.className = "flex items-end gap-2";
    const lbl = document.createElement("label");
    lbl.className = "block flex-1";
    lbl.appendChild(this._label("Resize home ×"));
    lbl.appendChild(
      this._numberInput(1, (v) => {
        this._scaleFactor = v;
      }, { step: "0.01", min: "0.1" }),
    );
    row.appendChild(lbl);

    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "hud-btn hud-btn--primary rounded-lg px-3 py-1.5 text-xs";
    btn.textContent = "Apply";
    btn.addEventListener("click", () => {
      const f = Number(this._scaleFactor) || 1;
      const next = mpp * f;
      if (next > 0) this.app.fire("ui:recalibrate", { meters_per_pixel: next });
    });
    row.appendChild(btn);
    el.appendChild(row);
  }

  _label(text) {
    const s = document.createElement("span");
    s.className = "ui-glass-field-label";
    s.textContent = text;
    return s;
  }

  _numberInput(value, onChange, { step = "0.1", min = "0.1" } = {}) {
    const input = document.createElement("input");
    input.type = "number";
    input.step = step;
    input.min = min;
    input.value = String(value ?? "");
    input.className = "ui-glass-input w-full text-xs";
    input.addEventListener("input", () => onChange(parseFloat(input.value)));
    // Don't let typing drive the camera / furniture.
    for (const ev of ["keydown", "keyup", "keypress"]) input.addEventListener(ev, (e) => e.stopPropagation());
    return input;
  }

  _dimField(key, value) {
    const wrap = document.createElement("label");
    wrap.className = "block";
    wrap.appendChild(this._label(key[0].toUpperCase() + key.slice(1)));
    wrap.appendChild(
      this._numberInput(value, (v) => {
        if (Number.isFinite(v)) this.spec[key] = v;
        this._updateFloor();
      }, { min: "1" }),
    );
    return wrap;
  }

  _openingRow(op, index) {
    const row = document.createElement("div");
    row.className = "ui-glass-card";

    // Header: type + wall select + remove.
    const head = document.createElement("div");
    head.className = "mb-1 flex items-center gap-1.5";
    const tag = document.createElement("span");
    tag.className = `rounded px-1.5 py-0.5 text-[10px] font-semibold ${op.type === "door" ? "bg-amber-400/20 text-amber-200" : "bg-sky-400/20 text-sky-200"}`;
    tag.textContent = op.type === "door" ? "Door" : "Window";
    head.appendChild(tag);

    const wallSel = document.createElement("select");
    wallSel.className = "ui-glass-input flex-1 py-0.5 text-[11px]";
    for (const side of ["south", "north", "east", "west"]) {
      const o = document.createElement("option");
      o.value = side;
      o.textContent = side;
      o.className = "";
      if (op.wall === side) o.selected = true;
      wallSel.appendChild(o);
    }
    wallSel.addEventListener("change", () => (this.spec.openings[index].wall = wallSel.value));
    head.appendChild(wallSel);

    const del = document.createElement("button");
    del.type = "button";
    del.className = "ui-glass-link shrink-0 hover:text-rose-500";
    del.textContent = "✕";
    del.title = "Remove";
    del.addEventListener("click", () => {
      this.spec.openings.splice(index, 1);
      this._renderForm();
    });
    head.appendChild(del);
    row.appendChild(head);

    // Fields: width / height / offset (+ sill for windows).
    const fields = [
      ["W", "width"],
      ["H", "height"],
      ["Off", "offset"],
    ];
    if (op.type === "window") fields.push(["Sill", "sill"]);

    const grid = document.createElement("div");
    grid.className = "grid grid-cols-4 gap-1";
    for (const [lbl, key] of fields) {
      const cell = document.createElement("label");
      cell.className = "block";
      cell.appendChild(this._label(lbl));
      cell.appendChild(
        this._numberInput(op[key] ?? 0, (v) => {
          if (Number.isFinite(v)) this.spec.openings[index][key] = v;
        }, { min: "0" }),
      );
      grid.appendChild(cell);
    }
    row.appendChild(grid);
    return row;
  }

  _addOpening(type) {
    if (!this.spec) return;
    const op =
      type === "door"
        ? { type: "door", wall: "south", offset: 1, width: 0.9, height: 2.1 }
        : { type: "window", wall: "north", offset: 1.5, width: 1.4, height: 1.2, sill: 0.9 };
    this.spec.openings.push(op);
    this._renderForm();
  }

  _updateFloor() {
    if (!this.analysisFloorEl || !this.spec) return;
    const area = (this.spec.width ?? 0) * (this.spec.depth ?? 0);
    this.analysisFloorEl.textContent = `${Math.round(area * 10) / 10} m²`;
  }

  _setStatus(text, busy, error) {
    if (!this.statusEl) return;
    if (!text) {
      this.statusEl.classList.add("hidden");
      return;
    }
    this.statusEl.classList.remove("hidden");
    this.statusEl.classList.toggle("flex", true);
    if (this.statusTextEl) this.statusTextEl.textContent = text;
    if (this.spinnerEl) this.spinnerEl.classList.toggle("hidden", !busy);
    if (this.statusTextEl) this.statusTextEl.classList.toggle("text-rose-300", Boolean(error));

    if (this.promptInput) this.promptInput.disabled = Boolean(busy);
    if (this.promptSend) this.promptSend.disabled = Boolean(busy);
    if (this.touchUploadBtn) this.touchUploadBtn.disabled = Boolean(busy);
    if (text) this._setTouchPlanStatus(text, { error });

    // Auto-hide a successful final status after a few seconds.
    clearTimeout(this._statusTimer);
    if (!busy && !error) {
      this._statusTimer = setTimeout(() => this.statusEl?.classList.add("hidden"), 4000);
    }
  }

  _updateHint() {
    if (!this.hintEl || this._placingAsset) return;
    if (this.selectedId) {
      this.hintEl.textContent = "Selected — move · Q/E rotate · Del to remove · click to deselect";
    } else {
      this.hintEl.textContent = "Pick a category, choose an item, then click in the room to place it.";
    }
    this.hintEl.classList.remove("text-sky-300", "text-rose-300");
  }

  /** Status pill room label: "Aspect · Kitchen | …" while inside a room. */
  _setStatusRoom(name) {
    if (!this.roomNameEl) return;
    const show = Boolean(name);
    this.roomNameEl.classList.toggle("hidden", !show);
    this.roomSepEl?.classList.toggle("hidden", !show);
    if (show) this.roomNameEl.textContent = name;
  }

  setBudget(total, count, saleTotal = null) {
    this._lastBudget = { total, count, saleTotal };
    if (this.budgetEl) {
      const list = Number(total ?? 0);
      const sale = Number(saleTotal);
      if (Number.isFinite(sale) && sale > 0 && sale < list) {
        // Real (discounted) total big, list total small + struck through.
        this.budgetEl.innerHTML = `
          <span class="hud-status__budget-sale">${formatGelPrice(sale)}</span>
          <span class="hud-status__budget-old">${formatGelPrice(list)}</span>`;
      } else {
        this.budgetEl.textContent = formatGelPrice(list);
      }
    }
    if (this.countEl) this.countEl.textContent = `${count ?? 0} item${count === 1 ? "" : "s"}`;
  }

  /** Left-rail savings summary: list total (crossed out) vs Aspect sale total. */
  setSavings(listTotal, saleTotal, count) {
    this._lastSavings = { list: listTotal, sale: saleTotal, count };
    if (!this.savingsEl) return;
    const list = Number(listTotal);
    const sale = Number(saleTotal);
    const show = (count ?? 0) > 0 && Number.isFinite(list) && Number.isFinite(sale) && sale < list;
    this.savingsEl.classList.toggle("hidden", !show);
    if (!show) return;
    const pct = Math.round((1 - sale / list) * 100);
    const nextNew = formatGelPrice(sale);
    if (this.savingsOldEl) this.savingsOldEl.textContent = formatGelPrice(list);
    if (this.savingsNewEl && this.savingsNewEl.textContent !== nextNew) {
      this.savingsNewEl.textContent = nextNew;
      this.savingsNewEl.classList.remove("is-updating");
      void this.savingsNewEl.offsetWidth;
      this.savingsNewEl.classList.add("is-updating");
    }
    if (this.savingsBadgeEl) {
      this.savingsBadgeEl.textContent = `−${pct}% · You save ${formatGelPrice(list - sale)}`;
    }
  }

  /** Per-room discount panel under the minimap: %, sale total, list total. */
  _renderRoomSavings() {
    if (!this.roomSavingsEl || !this.roomSavingsListEl) return;
    const rows = this._roomSavingsRows ?? [];
    this.roomSavingsEl.classList.toggle("hidden", rows.length === 0);
    if (!rows.length) {
      this.roomSavingsListEl.innerHTML = "";
      if (this.roomSavingsTotalEl) this.roomSavingsTotalEl.textContent = "";
      return;
    }
    let savedTotal = 0;
    const esc = (s) =>
      String(s ?? "").replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
    this.roomSavingsListEl.innerHTML = rows
      .map((r) => {
        savedTotal += r.list - r.sale;
        return `<li class="room-savings__row">
          <div class="room-savings__line">
            <span class="room-savings__room">${esc(r.name)}</span>
            <span class="room-savings__badge">−${r.pct}%</span>
            <span class="room-savings__prices">
              <span class="room-savings__new">${formatGelPrice(r.sale)}</span>
              <span class="room-savings__old">${formatGelPrice(r.list)}</span>
            </span>
          </div>
          <div class="room-savings__bar"><i style="width:${Math.min(100, r.pct)}%"></i></div>
        </li>`;
      })
      .join("");
    if (this.roomSavingsTotalEl) {
      this.roomSavingsTotalEl.textContent = `You save ${formatGelPrice(savedTotal)}`;
    }
  }

  setSelected(id, name) {
    this.selectedId = id;
    if (!this.selectionEl) return;
    if (id) {
      this.selectionEl.classList.remove("hidden");
      if (this.selectionTextEl) this.selectionTextEl.textContent = `Selected: ${name ?? id}`;
    } else {
      this.selectionEl.classList.add("hidden");
    }
    this._updateHint();
  }

  _bindCatalogSearch() {
    this.catalogSearch?.addEventListener("input", (e) => {
      this._catalogQuery = e.target.value;
      this._renderCatalogItems();
    });
    this.catalogSearch?.addEventListener("keydown", (e) => {
      if (e.key === "Escape") {
        this._catalogQuery = "";
        if (this.catalogSearch) this.catalogSearch.value = "";
        this._renderCatalogItems();
      }
    });
  }

  _catalogByCategory() {
    const map = new Map();
    for (const a of this.catalog) {
      const cat = a.category ?? "other";
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat).push(a);
    }
    return map;
  }

  renderCatalog(items) {
    this.catalog = items ?? [];
    if (this.catalogCountEl) this.catalogCountEl.textContent = String(this.catalog.length);
    this._catalogCategory = null;
    this._catalogQuery = "";
    if (this.catalogSearch) this.catalogSearch.value = "";
    this._renderCatalogCategories();
    const byCat = this._catalogByCategory();
    const order = ["living", "bedroom", "dining", "kitchen", "bathroom", "storage", "decor", "office"];
    const first = order.find((c) => byCat.has(c)) ?? [...byCat.keys()][0];
    if (first) this._openCatalogCategory(first);
    else {
      this.catalogItemsWrap?.classList.add("catalog-items-wrap--idle");
      this.hintEl?.classList.remove("hidden");
    }
  }

  _openCatalogCategory(category) {
    this._catalogCategory = category;
    this._catalogQuery = "";
    if (this.catalogSearch) this.catalogSearch.value = "";
    this.catalogItemsWrap?.classList.remove("catalog-items-wrap--idle");
    this.hintEl?.classList.add("hidden");
    this._renderCatalogCategories();
    this._renderCatalogItems();
  }

  _renderCatalogCategories() {
    if (!this.catalogCategoriesEl) return;
    const byCat = this._catalogByCategory();
    const order = ["living", "bedroom", "dining", "kitchen", "bathroom", "storage", "decor", "office"];
    const cats = order.filter((c) => byCat.has(c));
    for (const c of [...byCat.keys()].sort()) {
      if (!cats.includes(c)) cats.push(c);
    }
    this.catalogCategoriesEl.innerHTML = "";
    for (const cat of cats) {
      const items = byCat.get(cat) ?? [];
      const label = CATEGORY_LABELS[cat] ?? cat;
      const color = CATEGORY_COLORS[cat] ?? "#94a3b8";
      const active = this._catalogCategory === cat;
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = `catalog-cat-tile${active ? " catalog-cat-tile--active" : ""}`;
      btn.style.setProperty("--cat-color", color);
      btn.innerHTML = `
        <span class="catalog-cat-tile__count">${items.length}</span>
        <span class="catalog-cat-tile__icon">${categoryIcon(cat)}</span>
        <span class="catalog-cat-tile__label">${label}</span>`;
      btn.addEventListener("click", () => this._openCatalogCategory(cat));
      this.catalogCategoriesEl.appendChild(btn);
    }
  }

  _renderCatalogItems() {
    if (!this.catalogItemsEl || !this._catalogCategory) return;
    const q = this._catalogQuery.trim().toLowerCase();
    const items = this.catalog.filter((a) => {
      if (a.category !== this._catalogCategory) return false;
      if (!q) return true;
      return (
        (a.name ?? "").toLowerCase().includes(q) ||
        (a.brand ?? "").toLowerCase().includes(q) ||
        (a.asset_id ?? "").toLowerCase().includes(q)
      );
    });

    this.catalogItemsEl.innerHTML = "";
    if (this.catalogEmptyEl) {
      this.catalogEmptyEl.classList.toggle("hidden", items.length > 0);
    }

    for (const a of items) {
      const btn = document.createElement("button");
      btn.type = "button";
      const active = this._placingAsset === a.asset_id;
      btn.className = `catalog-item${active ? " catalog-item--active" : ""}`;
      // Real (sale) price big, list price crossed out — same deal as hover cards.
      const sale = aspectSalePrice(a.price, a.asset_id);
      const priceHtml = sale
        ? `<span class="catalog-item__price-row">
             <span class="catalog-item__price catalog-item__price--sale">${formatGelPrice(sale.sale)}</span>
             <span class="catalog-item__price-old">${formatGelPrice(sale.list)}</span>
             <span class="catalog-item__price-pct">−${sale.pct}%</span>
           </span>`
        : `<span class="catalog-item__price">${formatGelPrice(a.price ?? 0)}</span>`;
      btn.innerHTML = `
        <span class="catalog-item__thumb">
          <img class="catalog-item__img" src="${renderedThumb(a) ?? itemThumb(a)}" loading="lazy" decoding="async" alt="" />
        </span>
        <span class="catalog-item__body">
          <span class="catalog-item__name">${a.name ?? a.asset_id}</span>
          <span class="catalog-item__brand">${a.brand ?? ""}</span>
          ${priceHtml}
        </span>`;
      // Assets without a rendered product shot fall back to the procedural icon.
      const img = btn.querySelector(".catalog-item__img");
      img?.addEventListener("error", () => { img.src = itemThumb(a); }, { once: true });
      btn.addEventListener("click", () => {
        this.app.fire("ui:place-asset", { asset_id: a.asset_id });
      });
      this.catalogItemsEl.appendChild(btn);
    }
  }

  _flashHint(msg) {
    if (!this.hintEl) return;
    const prev = this.hintEl.textContent;
    this.hintEl.textContent = msg;
    setTimeout(() => {
      if (!this._placingAsset) this.hintEl.textContent = prev;
    }, 1800);
  }
}

export default Overlay;
